/**
 * `/remote-support` — XReach remote support. Port of the demo's
 * `renderRemote()` (line 3076).
 *
 * A field worker on a phone, a tablet or a head-mounted display streams what
 * they see to a product-support expert, who freezes frames and annotates them
 * back. Every session is logged with its outcome, and the ones that taught
 * somebody something become knowledge articles attached to the job card for
 * that component and model — so the next technician does not need the call.
 *
 * The four measures at the top are the argument: how many sessions, how many
 * fixed first time, how much road travel that avoided, and how much of it was
 * captured as knowledge rather than evaporating when the call ended.
 *
 * ## Data
 *
 * One domain — `service` — plus the lake catalogue for the nine session
 * reports, whose page thumbnail and body are fetched one document at a time.
 *
 * ## Declared omissions
 *
 * - **The annotated video.** The recordings are not in the extracted pack; the
 *   session report's page thumbnail stands in for the frame, and the report
 *   itself opens in the source drawer.
 * - **Leaving for the data browser.** The demo's report and article `open`
 *   links switched to the data view first (`show('data'); openDoc(…)`). The
 *   port opens both in the source drawer over this page, so the session stays
 *   in view; the work-order link opens its `wos` row the same way.
 *
 * @see src/versions/v1/modules/service/RemoteSupportScene.transform.ts
 * @see docs/logic/workflows/remote-support.md
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Headset } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  InlineError,
  SkeletonRows,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import type { LakeIndex } from '../../schema/document';
import { useLakeDoc } from '../../shared/lake-drawer';
import { useSourceDrawer } from '../issues/useSourceDrawer';
import { groupNum } from '../../shared/oem-engine';
import { MeasureTile, SceneSection, TonePill } from '../../shared/oem-tiles';
import { loadLakeDoc, loadLakeIndex, useOemData } from '../../shared/useOemData';
import {
  articleEntryFor,
  articleExcerpt,
  articleFor,
  articleRows,
  expertTallies,
  remoteSummary,
  reportFor,
  sessionRows,
  type RemoteSupportCtx,
  type SessionRow,
} from './RemoteSupportScene.transform';

/** Demo line 3079. The page's standfirst, verbatim. */
const STANDFIRST =
  'A field worker on a phone, tablet or head-mounted display streams what they see to a ' +
  'product-support expert, who freezes frames and annotates them back. Every session is logged ' +
  'with its outcome; the recording becomes a knowledge article so the next technician does not ' +
  'need the call.';

export function RemoteSupportScene() {
  const [params, setParams] = useSearchParams();
  const service = useOemData('service');
  const [lake, setLake] = useState<LakeIndex | null>(null);

  useEffect(() => {
    let live = true;
    loadLakeIndex().then(
      (index) => live && setLake(index),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, []);

  const ctx = useMemo<RemoteSupportCtx | null>(
    () =>
      service.data
        ? { sessions: service.data.xreach_sessions, kb: service.data.kb }
        : null,
    [service.data],
  );

  const rows = useMemo(() => (ctx ? sessionRows(ctx) : []), [ctx]);
  const summary = useMemo(() => (ctx ? remoteSummary(ctx) : null), [ctx]);
  const experts = useMemo(() => expertTallies(rows), [rows]);
  const articles = useMemo(() => (ctx ? articleRows(ctx) : []), [ctx]);

  // The demo selects on `session_id`, which is not unique; so does the URL,
  // because that is what a citation names. The first match wins, as it does
  // there, and the table keys on the composite so the twin row stays distinct.
  const requested = params.get('session');
  const selected = rows.find((r) => r.session.session_id === requested) ?? rows[0] ?? null;

  const select = (row: SessionRow) => {
    const next = new URLSearchParams(params);
    next.set('session', row.session.session_id);
    setParams(next, { replace: true });
  };

  return (
    <SurfacePageLayout width="full">
      <Commentable name="RemoteSupportScene">
        <div className="contents">
          <SurfacePageBody className="@container space-y-6" as="div">
            <SurfacePageHeader
              kicker="Workflows"
              title="Remote support"
              description={STANDFIRST}
              actions={
                <div className="flex items-center gap-2">
                  {summary ? (
                    <span className="mono rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] text-muted dark:bg-zinc-800">
                      {summary.sessions} sessions · {summary.firstTimeFixPct}% first-time fix ·{' '}
                      {groupNum(summary.kmAvoided)} km of travel avoided
                    </span>
                  ) : null}
                  <HelpAnchor doc="workflows/remote-support" />
                </div>
              }
            />

            <AskStrip view="remote" />

            {service.error ? (
              <InlineError>{`Remote-support sessions did not load: ${service.error.message}`}</InlineError>
            ) : !ctx || !summary ? (
              <SkeletonRows count={10} />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<Headset className="mx-auto h-8 w-8 text-zinc-400" strokeWidth={1.6} />}
                title="No sessions"
                description="The XReach log is empty."
              />
            ) : (
              <>
                <Commentable name="RemoteMeasures">
                  <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-4">
                    <MeasureTile
                      eyebrow="Sessions · 10 weeks"
                      value={summary.sessions}
                      unit={`${summary.thisMonth} this month`}
                      detail={experts.map((e) => `${e.short} ${e.sessions}`).join(' · ')}
                    />
                    <MeasureTile
                      eyebrow="First-time fix"
                      value={summary.firstTimeFixPct}
                      unit="%"
                      tone={summary.firstTimeFixPct >= 70 ? 'good' : 'warn'}
                      detail={`${summary.escalated} escalated to a site visit`}
                    />
                    <MeasureTile
                      eyebrow="Travel avoided"
                      value={summary.thousandKm}
                      unit="thousand km"
                      tone="good"
                      detail={`≈ A$${summary.audThousand}k at A$1.90/km including time · ${summary.hoursOnCamera} h on camera`}
                    />
                    <MeasureTile
                      eyebrow="Knowledge captured"
                      value={summary.articles}
                      unit="articles"
                      detail="attached to job cards on the same component and model"
                    />
                  </div>
                </Commentable>

                <div className="grid grid-cols-1 gap-6 @4xl:grid-cols-[minmax(0,1fr)_minmax(0,300px)]">
                  <Commentable name="RemoteSessions">
                    <div className="min-w-0">
                      <SceneSection title="Sessions" subtitle="newest first">
                        <Table className="min-w-[620px]">
                          <THead>
                            <TR>
                              <TH>Session</TH>
                              <TH>Started</TH>
                              <TH>Machine</TH>
                              <TH>Field → expert</TH>
                              <TH>Topic</TH>
                              <TH>Outcome</TH>
                            </TR>
                          </THead>
                          <TBody>
                            {rows.map((row) => (
                              <SessionTableRow
                                key={row.key}
                                row={row}
                                selected={selected?.key === row.key}
                                onSelect={select}
                              />
                            ))}
                          </TBody>
                        </Table>
                      </SceneSection>
                    </div>
                  </Commentable>

                  {selected ? (
                    <Commentable name="SessionDetail">
                      <div className="min-w-0">
                        <SessionDetail row={selected} ctx={ctx} lake={lake} />
                      </div>
                    </Commentable>
                  ) : null}
                </div>

                <Commentable name="KnowledgeArticles">
                  <div className="min-w-0">
                    <SceneSection
                      title="Knowledge base"
                      subtitle={`${articles.length} articles written out of sessions`}
                    >
                      <ul className="grid grid-cols-1 gap-3 @3xl:grid-cols-2 @6xl:grid-cols-3">
                        {articles.map(({ article, sources }) => (
                          <li
                            key={article.id}
                            className="rounded-xl border p-3"
                            style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                          >
                            <b className="mono block text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
                              {article.id}
                              {sources ? ` · from ${sources}` : ''}
                            </b>
                            <p
                              className="mt-1 text-[12.5px] font-medium"
                              style={{ color: 'var(--ink-primary)' }}
                            >
                              {article.title}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </SceneSection>
                  </div>
                </Commentable>
              </>
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

function SessionTableRow({
  row,
  selected,
  onSelect,
}: {
  row: SessionRow;
  selected: boolean;
  onSelect: (row: SessionRow) => void;
}) {
  const s = row.session;
  return (
    <TR
      aria-current={selected ? 'true' : undefined}
      style={selected ? { background: 'var(--accent-soft)' } : undefined}
    >
      <TD className="mono whitespace-nowrap">
        <button
          type="button"
          onClick={() => onSelect(row)}
          className="rounded underline decoration-dotted underline-offset-[3px] hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
          style={{ color: 'var(--accent)' }}
        >
          {s.session_id}
        </button>
      </TD>
      <TD className="mono whitespace-nowrap text-[11px]">{row.startedLabel}</TD>
      <TD className="whitespace-nowrap">
        <Link
          to={`/machines/${s.machine}`}
          className="mono underline decoration-dotted underline-offset-[3px] hover:decoration-solid"
          style={{ color: 'var(--accent)' }}
        >
          {s.machine}
        </Link>{' '}
        <span className="mono text-[10.5px]" style={{ color: 'var(--ink-tertiary)' }}>
          {row.mineShort}
        </span>
      </TD>
      <TD className="text-[11.5px]">
        {s.field_worker} → {s.expert.split(' ')[0]}
      </TD>
      <TD className="max-w-[26ch] text-[11.5px]">
        {row.topicShort}
        <span className="block text-[10.5px]" style={{ color: 'var(--ink-tertiary)' }}>
          {row.deviceShort} · {s.avg_bandwidth_mbps} Mbps
        </span>
      </TD>
      <TD>
        <TonePill tone={row.outcomeTone}>{row.outcomeShort}</TonePill>
      </TD>
    </TR>
  );
}

/** Demo line 3087. The selected session, with its report and article. */
function SessionDetail({
  row,
  ctx,
  lake,
}: {
  row: SessionRow;
  ctx: RemoteSupportCtx;
  lake: LakeIndex | null;
}) {
  const s = row.session;
  const openDoc = useLakeDoc();
  const { openDataset, pending } = useSourceDrawer();
  const report = useMemo(() => reportFor(lake?.entries ?? [], s), [lake, s]);
  const article = useMemo(() => articleFor(ctx, s), [ctx, s]);
  const articleEntry = useMemo(
    () => (article ? articleEntryFor(lake?.entries ?? [], article) : undefined),
    [lake, article],
  );
  const [thumb, setThumb] = useState<string | null>(null);

  useEffect(() => {
    setThumb(null);
    if (!report) return;
    let live = true;
    loadLakeDoc(report.slug).then(
      (doc) => live && setThumb(doc.thumb ?? null),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [report]);

  const facts: readonly (readonly [string, ReactNode])[] = [
    ['Field', `${s.field_worker} (${s.field_org})`],
    ['Expert', s.expert],
    ['Device', s.device],
    ['Network', `${s.network} · ${s.avg_bandwidth_mbps} Mbps · ${s.video_quality}`],
    ['Snapshots', `${s.snapshots} (${s.annotations} annotated)`],
    ['Outcome', `${s.outcome}${s.first_time_fix ? ' · first-time fix' : ''}`],
    [
      'Work order',
      <button
        key="wo"
        type="button"
        aria-busy={pending === 'wos'}
        onClick={() =>
          openDataset('wos', {
            title: `Work order ${s.linked_wo}`,
            filter: { wo_id: s.linked_wo },
          })
        }
        className="mono rounded underline decoration-dotted underline-offset-[3px] hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
        style={{ color: 'var(--accent)' }}
      >
        {s.linked_wo}
      </button>,
    ],
    ['Travel avoided', `${s.travel_avoided_km} km`],
    ['Rating', '★'.repeat(s.csat) + '☆'.repeat(5 - s.csat)],
  ];

  return (
    <aside
      className="flex flex-col gap-3 rounded-2xl border p-4"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
    >
      <div
        className="text-[10px] font-semibold uppercase tracking-[0.08em]"
        style={{ color: 'var(--ink-tertiary)' }}
      >
        {s.session_id} · {s.duration_min} min
      </div>
      <b className="text-[13.5px]" style={{ color: 'var(--ink-primary)' }}>
        {s.topic}
      </b>

      <dl className="grid grid-cols-[minmax(0,88px)_minmax(0,1fr)] gap-x-3 gap-y-1 text-[11.5px]">
        {facts.map(([label, value]) => (
          <div key={label} className="contents">
            <dt style={{ color: 'var(--ink-tertiary)' }}>{label}</dt>
            <dd className="min-w-0" style={{ color: 'var(--ink-secondary)' }}>
              {value}
            </dd>
          </div>
        ))}
      </dl>

      {report ? (
        <div className="space-y-2">
          {thumb ? (
            <img
              src={`data:image/webp;base64,${thumb}`}
              alt={`First page of the ${s.session_id} session report`}
              className="w-full rounded border"
              style={{ borderColor: 'var(--hairline)' }}
            />
          ) : null}
          <button
            type="button"
            onClick={() =>
              openDoc({
                file: report.file,
                connector: report.conn,
                title: `${s.session_id} session report`,
              })
            }
            className="w-full rounded-lg border px-2.5 py-1.5 text-[11.5px] transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
            style={{ borderColor: 'var(--hairline)', color: 'var(--ink-secondary)' }}
          >
            Open the session report
          </button>
        </div>
      ) : (
        <p
          className="rounded-lg border border-dashed px-2.5 py-3 text-center text-[11.5px]"
          style={{ borderColor: 'var(--hairline)', color: 'var(--ink-tertiary)' }}
        >
          No session report filed. Nine of the thirty-six sessions produced one.
        </p>
      )}

      {article ? (
        <div className="space-y-1.5">
          <div
            className="text-[10px] font-semibold uppercase tracking-[0.08em]"
            style={{ color: 'var(--ink-tertiary)' }}
          >
            Knowledge article
          </div>
          <b className="block text-[12px]" style={{ color: 'var(--ink-primary)' }}>
            {article.id} — {article.title}
          </b>
          <p
            className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg p-2 text-[11.5px] leading-[1.55]"
            style={{ background: 'var(--surface-1)', color: 'var(--ink-secondary)' }}
          >
            {articleExcerpt(article)}
          </p>
          <button
            type="button"
            onClick={() =>
              openDoc({
                file: article.file,
                connector: articleEntry?.conn ?? 'XReach · remote support',
                title: `${article.id} — ${article.title}`,
              })
            }
            aria-label={`Open ${article.id} from the data lake`}
            className="rounded text-[11.5px] font-medium underline decoration-dotted underline-offset-[3px] hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
            style={{ color: 'var(--accent)' }}
          >
            open
          </button>
        </div>
      ) : (
        <p className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
          No knowledge article — a verification call rather than a new problem.
        </p>
      )}
    </aside>
  );
}
